import React, { useState, useEffect, useRef } from 'react';
import nfcStateManager, { NFC_STATES, NFC_ERRORS } from '../utils/nfcStateManager.js';
import nfcConfigManager from '../utils/nfcConfigManager.js';
import '../styles/NFCScanModal.css';

const NFCScanModal = ({ isOpen, onClose, onSuccess, onError }) => {
  const [nfcState, setNfcState] = useState(nfcStateManager.getState());
  const [showInstructions, setShowInstructions] = useState(true);
  const [progress, setProgress] = useState(0);
  const [hapticSupported, setHapticSupported] = useState(false);
  const unsubscribeRef = useRef(null);

  useEffect(() => {
    // Check haptic feedback support
    setHapticSupported('vibrate' in navigator);

    // Subscribe to NFC state changes
    unsubscribeRef.current = nfcStateManager.subscribe((state) => {
      setNfcState(state);
      setProgress(nfcStateManager.getProgress());

      // Handle haptic feedback
      if (hapticSupported) {
        if (state.state === NFC_STATES.CHIP_DETECTED) {
          navigator.vibrate([100, 50, 100]); // Success pattern
        } else if (state.state === NFC_STATES.ERROR) {
          navigator.vibrate([200, 100, 200, 100, 200]); // Error pattern
        }
      }

      // Auto-close on success after delay
      if (state.state === NFC_STATES.SUCCESS) {
        setTimeout(() => {
          onSuccess?.(state.data);
          handleClose();
        }, 2000);
      }

      // Handle errors
      if (state.state === NFC_STATES.ERROR) {
        setTimeout(() => {
          onError?.(state.error);
        }, 100);
      }
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [hapticSupported, onSuccess, onError]);

  // Auto-start scan when modal opens
  useEffect(() => {
    if (isOpen && nfcState.state === NFC_STATES.IDLE) {
      handleStartScan();
    }
  }, [isOpen]);

  const handleStartScan = async () => {
    setShowInstructions(false);
    await nfcStateManager.startScan();
  };

  const handleClose = () => {
    nfcStateManager.reset();
    setShowInstructions(true);
    setProgress(0);
    onClose?.();
  };

  const handleCancel = () => {
    nfcStateManager.cancel();
    handleClose();
  };

  const handleRetry = () => {
    nfcStateManager.reset();
    setShowInstructions(false);
    setTimeout(() => {
      nfcStateManager.startScan();
    }, 500);
  };

  // Get current status message
  const getStatusMessage = () => {
    switch (nfcState.state) {
      case NFC_STATES.CHECKING_SUPPORT:
        return 'Checking NFC support...';
      case NFC_STATES.REQUESTING_PERMISSION:
        return 'Requesting NFC permission...';
      case NFC_STATES.INITIALIZING_SCAN:
        return 'Initializing scanner...';
      case NFC_STATES.SCANNING:
        return nfcState.data?.instructions || 'Scanning for NFC device...';
      case NFC_STATES.CHIP_DETECTED:
        return nfcState.data?.message || 'NFC device detected!';
      case NFC_STATES.VALIDATING_CHIP:
        return nfcState.data?.message || 'Validating NFC credentials...';
      case NFC_STATES.PROCESSING_ACTIVATION:
        return nfcState.data?.message || 'Processing activation...';
      case NFC_STATES.SUCCESS:
        return nfcState.data?.message || 'NFC authentication successful!';
      case NFC_STATES.ERROR:
        return nfcState.error || 'An error occurred';
      case NFC_STATES.TIMEOUT:
        return 'Scan timeout. Please try again.';
      case NFC_STATES.CANCELLED:
        return 'Scan cancelled by user';
      default:
        return 'Ready to scan';
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    switch (nfcState.state) {
      case NFC_STATES.CHECKING_SUPPORT:
      case NFC_STATES.REQUESTING_PERMISSION:
      case NFC_STATES.INITIALIZING_SCAN:
        return '⚙️';
      case NFC_STATES.SCANNING:
        return '📡';
      case NFC_STATES.CHIP_DETECTED:
        return '📱';
      case NFC_STATES.VALIDATING_CHIP:
      case NFC_STATES.PROCESSING_ACTIVATION:
        return '🔄';
      case NFC_STATES.SUCCESS:
        return '✅';
      case NFC_STATES.ERROR:
      case NFC_STATES.TIMEOUT:
        return '❌';
      case NFC_STATES.CANCELLED:
        return '🚫';
      default:
        return '📱';
    }
  };

  // Check if operation is in progress
  const isInProgress = nfcStateManager.isOperationInProgress();

  // Check if operation completed (success or error)
  const isCompleted = [
    NFC_STATES.SUCCESS,
    NFC_STATES.ERROR,
    NFC_STATES.TIMEOUT,
    NFC_STATES.CANCELLED
  ].includes(nfcState.state);

  if (!isOpen) return null;

  return (
    <div className="nfc-scan-modal-overlay">
      <div className="nfc-scan-modal">
        <div className="nfc-scan-header">
          <h3>🎫 NFC Scanner</h3>
          <button
            className="nfc-scan-close-btn"
            onClick={handleClose}
            disabled={isInProgress}
          >
            ×
          </button>
        </div>

        <div className="nfc-scan-content">
          {showInstructions && nfcState.state === NFC_STATES.IDLE ? (
            // Instructions screen
            <div className="nfc-instructions">
              <div className="nfc-instruction-icon">📱</div>
              <h4>Ready to Scan</h4>
              <p>Make sure your LOS DIAS merchandise NFC tag is ready and tap "Start Scan" to begin.</p>

              <div className="nfc-requirements">
                <div className="requirement-item">
                  <span className="req-icon">✓</span>
                  <span>NFC must be enabled on your device</span>
                </div>
                <div className="requirement-item">
                  <span className="req-icon">✓</span>
                  <span>Hold tag close to device sensor</span>
                </div>
                <div className="requirement-item">
                  <span className="req-icon">✓</span>
                  <span>Keep tag steady until confirmation</span>
                </div>
              </div>

              <button
                className="nfc-start-scan-btn"
                onClick={handleStartScan}
              >
                📡 Start Scan
              </button>
            </div>
          ) : (
            // Scanning/Results screen
            <div className="nfc-scan-status">
              <div className="nfc-status-icon-container">
                <div className={`nfc-status-icon ${isInProgress ? 'scanning' : ''}`}>
                  {getStatusIcon()}
                </div>

                {nfcState.state === NFC_STATES.SCANNING && (
                  <div className="nfc-scan-animation">
                    <div className="scan-wave"></div>
                    <div className="scan-wave"></div>
                    <div className="scan-wave"></div>
                  </div>
                )}
              </div>

              <div className="nfc-status-message">
                <h4>{getStatusMessage()}</h4>

                {nfcState.state === NFC_STATES.SCANNING && (
                  <p className="scan-instruction">
                    Hold your NFC tag near the device sensor
                  </p>
                )}

                {nfcState.state === NFC_STATES.SUCCESS && nfcState.data && (
                  <div className="success-details">
                    <p className="success-type">
                      {nfcState.data.type === 'nfc_permanent' ?
                        '🏆 Permanent Premium Activated!' :
                        '🎁 7-Day Premium Trial Activated!'}
                    </p>
                    {nfcState.data.expiresAt && (
                      <p className="success-expires">
                        Expires: {new Date(nfcState.data.expiresAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                )}

                {nfcState.state === NFC_STATES.ERROR && (
                  <div className="error-details">
                    <p className="error-message">{nfcState.error}</p>

                    {nfcState.data?.errorType === NFC_ERRORS.NOT_SUPPORTED && (
                      <div className="error-help">
                        <p>Your device doesn't support NFC or it's disabled.</p>
                        <p>Try enabling NFC in your device settings.</p>
                      </div>
                    )}

                    {nfcState.data?.errorType === NFC_ERRORS.INVALID_CHIP && (
                      <div className="error-help">
                        <p>Make sure you're using a valid LOS DIAS merchandise NFC tag.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Progress bar */}
              {isInProgress && (
                <div className="nfc-progress-container">
                  <div className="nfc-progress-bar">
                    <div
                      className="nfc-progress-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <span className="nfc-progress-text">{progress}%</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="nfc-scan-footer">
          {nfcState.state === NFC_STATES.IDLE && showInstructions ? (
            <button
              className="nfc-cancel-btn"
              onClick={handleClose}
            >
              Cancel
            </button>
          ) : isInProgress ? (
            <button
              className="nfc-cancel-btn"
              onClick={handleCancel}
            >
              Cancel Scan
            </button>
          ) : isCompleted ? (
            <div className="nfc-completed-actions">
              {nfcState.state === NFC_STATES.ERROR && (
                <button
                  className="nfc-retry-btn"
                  onClick={handleRetry}
                >
                  🔄 Retry
                </button>
              )}
              <button
                className="nfc-done-btn"
                onClick={handleClose}
              >
                {nfcState.state === NFC_STATES.SUCCESS ? 'Done' : 'Close'}
              </button>
            </div>
          ) : null}
        </div>

        {/* Debug info in development */}
        {process.env.NODE_ENV === 'development' && (
          <div className="nfc-debug">
            <small>State: {nfcState.state} | Progress: {progress}%</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default NFCScanModal;